import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileBottomNav from '@/components/MobileBottomNav';
import { useEffect, useState, type FormEvent } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { IconArmchair as Armchair, IconArrowRight as ArrowRight, IconArrowUpRight as ArrowUpRight, IconBriefcase as Briefcase, IconBarbell as Dumbbell, IconFlask as FlaskConical, IconHeartHandshake as HeartHandshake, IconHeartRateMonitor as HeartPulse, IconMusic as Music, IconPalette as Palette, IconScan as ScanLine, IconSearch as SearchIcon, IconTicket as Ticket, IconUsers as Users, IconWallet as Wallet, IconX as X } from '@tabler/icons-react';
import EventCard from '@/components/EventCard';
import { fetchActiveCategories, fetchEvents, searchEventsByCoordinates } from '@/apiEndpoints';
import moment from 'moment';

const getSiteName = () => {
    return (import.meta as any).env?.VITE_SITE_NAME || 'EventUp';
};

const wrap = 'mx-auto w-full max-w-[1200px] px-4 xl:px-0';

const rotatingWords = ['night out.', 'front row.', 'obsession.', 'people.', 'adventure.'];

const marqueeWords = ['Live music', 'Rooftop parties', 'Workshops', 'Festivals', 'Comedy nights', 'Art walks', 'Meetups', 'Game days', 'Wellness', 'Tech talks'];

const vibes = [
    { key: 'music', label: 'Music', Icon: Music, tile: 'from-[#ff3d6e] to-[#ff9a3c] text-white' },
    { key: 'art', label: 'Arts & Culture', Icon: Palette, tile: 'from-[#6d4aff] to-[#c56bff] text-white' },
    { key: 'sports', label: 'Sports & Fitness', Icon: Dumbbell, tile: 'from-[#c6f432] to-[#5be37d] text-[#0a0a0a]' },
    { key: 'networking', label: 'Networking', Icon: Users, tile: 'from-[#007D85] to-[#22c9cd] text-white' },
    { key: 'business', label: 'Business', Icon: Briefcase, tile: 'from-[#111111] to-[#3a3a3a] text-white' },
    { key: 'social', label: 'Social & Community', Icon: HeartHandshake, tile: 'from-[#ffb800] to-[#ffe14d] text-[#0a0a0a]' },
    { key: 'health', label: 'Health & Wellness', Icon: HeartPulse, tile: 'from-[#ff7eb3] to-[#ffc3d8] text-[#0a0a0a]' },
    { key: 'science', label: 'Science & Tech', Icon: FlaskConical, tile: 'from-[#1e40ff] to-[#22d3ee] text-white' },
];

const categoryChips = [{ key: '', label: 'Everything' }, ...vibes.map(({ key, label }) => ({ key, label }))];

const priceOptions = [
    { label: 'All', key: 'all' },
    { label: 'Free', key: 'free' },
    { label: 'Paid', key: 'paid' },
];

const dateOptions = [
    { label: 'Anytime', key: '' },
    { label: 'Today', key: 'today' },
    { label: 'Weekend', key: 'weekend' },
];

const organizerFeatures = [
    { Icon: Ticket, title: 'Ticketing', text: 'Paid, free and donation tickets with promo codes.' },
    { Icon: Armchair, title: 'Seat maps', text: 'Reserved seating designed in a visual studio.' },
    { Icon: ScanLine, title: 'Check-in', text: 'Scan QR tickets at the door from any phone.' },
    { Icon: Wallet, title: 'Payouts', text: 'Get paid out securely through Stripe.' },
];

const bentoCols: Record<number, string> = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4' };

function RotatingWord() {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        const id = window.setInterval(() => setIndex(i => (i + 1) % rotatingWords.length), 2600);
        return () => window.clearInterval(id);
    }, []);
    return (
        <span className="relative inline-flex overflow-hidden pb-[0.08em] align-bottom">
            <span key={index} className="inline-block animate-word-in motion-reduce:animate-none text-primary dark:text-nightAccent">
                {rotatingWords[index]}
            </span>
        </span>
    );
}

function Segmented({ options, value, onChange, label }: {
    options: { label: string; key: string }[];
    value: string;
    onChange: (key: string) => void;
    label: string;
}) {
    return (
        <div role="radiogroup" aria-label={label} className="inline-flex shrink-0 rounded-full bg-grey99 p-1 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]">
            {options.map(option => {
                const active = option.key === value;
                return (
                    <button
                        key={option.key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => onChange(option.key)}
                        className={`h-9 rounded-full px-4 text-sm font-semibold whitespace-nowrap transition-all ${active
                            ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg'
                            : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'}`}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

function PosterTile({ event, large }: { event: any; large: boolean }) {
    const start = event.times?.[0]?.startDate ? moment(event.times[0].startDate) : null;
    const place = event.showOnlyCity === 1 ? (event.address?.city || event.venue) : (event.venue || event.address?.city);
    return (
        <Link
            to={`/event/${event._id}`}
            className={`group relative block shrink-0 snap-start overflow-hidden rounded-[28px] bg-grey10 w-[80%] sm:w-[46%] aspect-[4/5] lg:w-auto lg:aspect-auto ${large ? 'lg:col-span-2 lg:row-span-2' : ''}`}
        >
            <img
                src={event.attachments.banner.link}
                decoding="async"
                loading="lazy"
                alt={event.title || ''}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            {start && (
                <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] text-[#0a0a0a]">
                    {start.format('ddd D MMM')}
                </span>
            )}
            <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:rotate-45">
                <ArrowUpRight className="h-5 w-5" />
            </span>
            <div className="absolute inset-x-0 bottom-0 p-5 lg:p-6">
                <h3 className={`line-clamp-2 font-bold leading-[1.05] tracking-[-0.03em] text-white ${large ? 'text-2xl lg:text-[40px]' : 'text-xl lg:text-2xl'}`}>
                    {event.title}
                </h3>
                {place && <p className="mt-2 truncate text-sm capitalize text-white/70">{place}</p>}
            </div>
        </Link>
    );
}

function EventCardSkeleton() {
    return (
        <div className="px-0 py-4 sm:p-4">
            <div className="aspect-[4/5] animate-pulse rounded-[28px] bg-grey99 dark:bg-white/[0.06]" />
        </div>
    );
}

export default function Home({ iframe }: { iframe: boolean }) {
    const navigate = useNavigate();
    const [price, setPrice] = useState('all');
    const [category, setCategory] = useState('');
    const [selectedLocation, setSelectedLocation] = useState<any>(null);
    const [geolocationStatus, setGeolocationStatus] = useState<string>('');
    const [quickDate, setQuickDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const handleLocationChange = (location: any) => {
        setSelectedLocation(location);
        setGeolocationStatus('');
    };

    const handleLikeChange = (_eventId: string, _likes: number, _isLiked: boolean) => { };

    const handleSearch = (e: FormEvent) => {
        e.preventDefault();
        const q = searchTerm.trim();
        navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    };

    const scrollToEvents = () => {
        document.getElementById('whats-on')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const pickVibe = (key: string) => {
        setCategory(key);
        scrollToEvents();
    };

    let dateParam: string | undefined;
    if (quickDate === 'today') {
        dateParam = new Date().toISOString().split('T')[0] + ',' + new Date().toISOString().split('T')[0];
    } else if (quickDate === 'weekend') {
        const today = new Date();
        const nextWeekend = new Date(today);
        nextWeekend.setDate(today.getDate() + ((1 + 7 - today.getDay()) % 7));
        const startOfWeekend = moment(nextWeekend).endOf('week').add(-1, 'day').format('YYYY-MM-DD');
        const endOfWeekend = moment(nextWeekend).endOf('week').add(1, 'day').format('YYYY-MM-DD');
        dateParam = `${startOfWeekend} 00:00,${endOfWeekend} 23:59`;
    }
    else {
        dateParam = new Date().toISOString().split('T')[0];
    }

    // Use react-query's useInfiniteQuery for paginated data
    const {
        data,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['events', price, category, dateParam, selectedLocation],
        queryFn: async ({ pageParam = 0 }) => {

            // If we have a selected location with coordinates, use coordinate-based search
            if (selectedLocation && selectedLocation.lat && selectedLocation.lng && selectedLocation.city !== 'My Location') {

                const res = await searchEventsByCoordinates({
                    lat: selectedLocation.lat,
                    lng: selectedLocation.lng,
                    radius: 100, // Fixed radius of 50km
                    page: pageParam,
                    limit: 8,
                    category,
                    search: undefined,
                    price,
                    date: dateParam
                });
                return res;
            } else if (selectedLocation && selectedLocation.city === 'My Location') {
                console.log('selectedLocation', selectedLocation);
                setGeolocationStatus('Getting your location...');

                // Handle "My Location" - get user's current location and search nearby
                if (navigator.geolocation) {
                    return new Promise((resolve) => {
                        const options = {
                            enableHighAccuracy: true,
                            timeout: 2000, // 2 seconds timeout
                            maximumAge: 300000, // 5 minutes cache
                            requireAuthorization: true
                        };

                        navigator.geolocation.getCurrentPosition(
                            async (position) => {
                                console.log('position', position);
                                setGeolocationStatus('Location found! Searching nearby events...');

                                try {
                                    const res = await searchEventsByCoordinates({
                                        lat: position.coords.latitude,
                                        lng: position.coords.longitude,
                                        radius: 50,
                                        page: pageParam,
                                        limit: 8,
                                        category,
                                        search: undefined,
                                        price,
                                        date: dateParam
                                    });
                                    setGeolocationStatus('');
                                    resolve(res);
                                } catch (error) {
                                    console.error('Coordinate search failed:', error);
                                    setGeolocationStatus('Search failed, showing all events...');
                                    // Fallback to regular search if coordinate search fails
                                    const res = await fetchEvents({
                                        page: pageParam,
                                        price,
                                        category,
                                        date: dateParam
                                    });
                                    resolve(res);
                                }
                            },
                            async (error) => {
                                console.error('Geolocation error:', error);

                                // Provide specific error messages
                                let errorMessage = 'Unable to get your location. ';
                                switch (error.code) {
                                    case error.PERMISSION_DENIED:
                                        errorMessage += 'Please allow location access in your browser settings.';
                                        break;
                                    case error.POSITION_UNAVAILABLE:
                                        errorMessage += 'Location information is unavailable.';
                                        break;
                                    case error.TIMEOUT:
                                        errorMessage += 'Location request timed out.';
                                        break;
                                    default:
                                        errorMessage += 'An unknown error occurred.';
                                        break;
                                }

                                console.warn(errorMessage);
                                setGeolocationStatus(errorMessage);

                                // Clear status after 5 seconds
                                setTimeout(() => setGeolocationStatus(''), 2000);

                                // Fallback to regular search
                                const res = await fetchEvents({
                                    page: pageParam,
                                    price,
                                    category,
                                    date: dateParam
                                });
                                resolve(res);
                            },
                            options
                        );
                    });
                } else {
                    // Fallback to regular search if geolocation is not supported
                    const res = await fetchEvents({
                        page: pageParam,
                        price,
                        category,
                        date: dateParam
                    });
                    return res;
                }
            } else {
                // Fallback to regular search
                const res = await fetchEvents({
                    page: pageParam,
                    price,
                    category,
                    date: dateParam
                });
                return res;
            }
        },
        getNextPageParam: (lastPage) => lastPage.nextPage,
        staleTime: 1000 * 60,
        initialPageParam: 0,
    });

    // Flatten all loaded pages
    const nextStartTime = (event: any) => {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const now = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        const starts: string[] = (event.times || []).map((t: any) => t.startDate).filter(Boolean).sort();
        return starts.find(d => d >= now) || starts[0] || '9999';
    };
    const apiEvents = data
        ? data.pages.flatMap(page => Array.isArray(page.data) ? page.data : [])
            .sort((a: any, b: any) => nextStartTime(a).localeCompare(nextStartTime(b)))
        : [];
    const userInfos = data
        ? data.pages.flatMap(page => Array.isArray(page.config.userInfos) ? page.config.userInfos : [])
        : [];
    const organizerInfos = data
        ? data.pages.flatMap(page => Array.isArray(page.config.organizerInfos) ? page.config.organizerInfos : [])
        : [];
    const featured = apiEvents.filter((e: any) => e?.attachments?.banner?.link).slice(0, 5);
    const city = selectedLocation?.city && selectedLocation.city !== 'My Location' ? selectedLocation.city : '';
    const { data: activeCategories } = useQuery({
        queryKey: ['active-categories'],
        queryFn: fetchActiveCategories,
        staleTime: 15 * 60 * 1000,
    });
    const visibleChips = activeCategories
        ? categoryChips.filter(c => c.key === '' || c.key === category || activeCategories.includes(c.key))
        : categoryChips;

    const hasActiveFilters = price !== 'all' || category !== '' || quickDate !== '';

    return (
        <div className={iframe ? undefined : 'bg-white dark:bg-nightBg transition-colors'}>
            {iframe ? null : <Header onLocationChange={handleLocationChange} />}

            <main className="min-h-screen overflow-x-clip">
                {iframe ? null : (
                    <>
                        <section className="relative isolate">
                            <div className="pointer-events-none absolute -top-48 left-1/2 -z-10 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(34,201,205,0.2),transparent)] dark:bg-[radial-gradient(closest-side,rgba(34,201,205,0.15),transparent)]" />
                            <div className={`${wrap} pt-10 sm:pt-16 lg:pt-20`}>
                                <div className="inline-flex items-center gap-2.5 rounded-full bg-grey99 py-1.5 pl-2 pr-4 text-sm font-medium text-grey20 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:text-nightTextSecondary dark:ring-white/[0.06]">
                                    <span className="relative flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 dark:bg-nightAccent/20">
                                        <span className="absolute h-2 w-2 animate-ping rounded-full bg-primary opacity-60 dark:bg-nightAccent" />
                                        <span className="h-2 w-2 rounded-full bg-primary dark:bg-nightAccent" />
                                    </span>
                                    {city ? `Live in ${city}` : 'Happening near you'}
                                </div>

                                <h1 className="mt-6 text-[clamp(44px,9.2vw,132px)] font-bold leading-[0.92] tracking-[-0.055em] text-grey10 dark:text-white">
                                    Find your next
                                    <br />
                                    <RotatingWord />
                                </h1>

                                <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_560px] lg:items-end">
                                    <p className="max-w-[440px] text-lg leading-relaxed text-grey40 dark:text-nightMuted">
                                        Concerts, parties, workshops and meetups. Everything worth leaving the house for, in one place.
                                    </p>
                                    <div>
                                        <form
                                            onSubmit={handleSearch}
                                            className="flex h-16 items-center gap-3 rounded-full bg-white pl-6 pr-2 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.06] transition-shadow focus-within:ring-2 focus-within:ring-primary/50 dark:bg-white/[0.06] dark:shadow-none dark:ring-white/10 dark:focus-within:ring-nightAccent/60"
                                        >
                                            <SearchIcon className="h-5 w-5 shrink-0 text-grey50 dark:text-nightMuted" />
                                            <input
                                                value={searchTerm}
                                                onChange={e => setSearchTerm(e.target.value)}
                                                placeholder="Artists, events, venues"
                                                aria-label="Search events"
                                                className="min-w-0 flex-1 bg-transparent text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted"
                                            />
                                            <button
                                                type="submit"
                                                aria-label="Search"
                                                className="flex h-12 shrink-0 items-center gap-2 rounded-full bg-grey10 px-5 text-sm font-semibold text-white transition-colors hover:bg-black dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70"
                                            >
                                                <span className="hidden sm:inline">Search</span>
                                                <ArrowRight className="h-4 w-4" />
                                            </button>
                                        </form>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {[
                                                { label: 'Tonight', onClick: () => { setQuickDate('today'); scrollToEvents(); } },
                                                { label: 'This weekend', onClick: () => { setQuickDate('weekend'); scrollToEvents(); } },
                                                { label: 'Free events', onClick: () => { setPrice('free'); scrollToEvents(); } },
                                                { label: 'Live music', onClick: () => pickVibe('music') },
                                            ].map(chip => (
                                                <button
                                                    key={chip.label}
                                                    type="button"
                                                    onClick={chip.onClick}
                                                    className="h-9 rounded-full px-4 text-sm font-medium text-grey20 ring-1 ring-grey90 transition-colors hover:bg-grey10 hover:text-white hover:ring-grey10 dark:text-nightTextSecondary dark:ring-white/15 dark:hover:bg-white dark:hover:text-nightBg dark:hover:ring-white"
                                                >
                                                    {chip.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {featured.length > 0 && (
                                <div className={`${wrap} mt-14 sm:mt-20`}>
                                    <div className="mb-5 flex items-end justify-between">
                                        <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted">Trending now</h2>
                                        <button type="button" onClick={scrollToEvents} className="inline-flex items-center gap-1.5 text-sm font-semibold text-grey10 hover:text-primary dark:text-white dark:hover:text-nightAccent">
                                            See all <ArrowRight className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <div className={`-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:grid lg:h-[560px] lg:gap-4 lg:overflow-visible lg:px-0 lg:pb-0 ${featured.length >= 5 ? 'lg:grid-cols-4 lg:grid-rows-2' : bentoCols[featured.length]}`}>
                                        {featured.map((event: any, i: number) => (
                                            <PosterTile key={event._id} event={event} large={i === 0 && featured.length >= 5} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </section>

                        <section aria-hidden className="relative mt-16 py-6 sm:mt-24">
                            <div className="-mx-8 -rotate-[2deg] bg-grey10 py-4 dark:bg-nightAccent">
                                <div className="flex w-max animate-marquee transform-gpu motion-reduce:animate-none">
                                    {[0, 1].map(copy => (
                                        <div key={copy} className="flex shrink-0 items-center">
                                            {marqueeWords.map(word => (
                                                <span key={`${copy}-${word}`} className="flex items-center whitespace-nowrap text-2xl font-bold uppercase tracking-[-0.02em] text-white dark:text-nightBg sm:text-3xl">
                                                    <span className="px-6">{word}</span>
                                                    <span className="text-nightAccent dark:text-nightBg/60">✦</span>
                                                </span>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    </>
                )}

                <section id="whats-on" className={`${wrap} ${iframe ? 'pt-4' : 'mt-20 sm:mt-28'} scroll-mt-24`}>
                    {!iframe && (
                        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                            <h2 className="text-[clamp(36px,5.5vw,72px)] font-bold leading-[0.95] tracking-[-0.045em] text-grey10 dark:text-white">
                                What's on{city && <span className="text-grey70 dark:text-white/30"> in {city}</span>}.
                            </h2>
                            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:px-0">
                                <Segmented label="Date" options={dateOptions} value={quickDate} onChange={setQuickDate} />
                                <Segmented label="Price" options={priceOptions} value={price} onChange={setPrice} />
                            </div>
                        </div>
                    )}

                    <div className="-mx-4 mt-6 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden xl:mx-0 xl:px-0">
                        <div className="flex gap-2">
                            {visibleChips.map(({ key, label }) => {
                                const active = category === key;
                                return (
                                    <button
                                        key={key || 'all'}
                                        type="button"
                                        onClick={() => setCategory(key)}
                                        aria-pressed={active}
                                        className={`h-10 shrink-0 rounded-full px-4 text-sm font-semibold transition-colors ${active
                                            ? 'bg-primary text-white dark:bg-nightAccent dark:text-nightBg'
                                            : 'bg-grey99 text-grey20 hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10'}`}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {geolocationStatus && (
                        <div className="mt-5 flex items-center gap-2.5 rounded-2xl bg-primary99 px-4 py-3 text-sm text-primary20 dark:bg-white/[0.06] dark:text-nightAccent">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-primary dark:bg-nightAccent" />
                            {geolocationStatus}
                        </div>
                    )}

                    <div className={`mt-4 grid grid-cols-1 content-start sm:-mx-4 sm:grid-cols-2 lg:grid-cols-3 ${!isLoading && apiEvents.length === 0 ? '' : 'min-h-[600px]'}`}>
                        {isLoading ? (
                            Array.from({ length: 6 }).map((_, i) => <EventCardSkeleton key={i} />)
                        ) : apiEvents.length === 0 ? (
                            <div className="col-span-full mt-4 flex flex-col items-center rounded-[28px] bg-grey99 px-6 py-20 text-center dark:bg-white/[0.04] sm:mx-4">
                                <div className="text-[clamp(32px,4vw,48px)] font-bold tracking-[-0.04em] text-grey10 dark:text-white">Nothing here. Yet.</div>
                                <p className="mt-3 max-w-sm text-base text-grey40 dark:text-nightMuted">
                                    No events match these filters. Try another date, vibe or price.
                                </p>
                                {hasActiveFilters && (
                                    <button
                                        type="button"
                                        onClick={() => { setPrice('all'); setCategory(''); setQuickDate(''); }}
                                        className="mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-grey10 px-6 text-sm font-semibold text-white hover:bg-black dark:bg-white dark:text-nightBg"
                                    >
                                        <X className="h-4 w-4" /> Clear filters
                                    </button>
                                )}
                            </div>
                        ) : (
                            apiEvents.map((event, i) => {
                                const userInfo = userInfos.find(x => x.id == event.userId);
                                const organizerInfo = organizerInfos.find(x => x._id == event.organizerId);
                                return (
                                    <EventCard
                                        iframe={iframe}
                                        key={event._id || i}
                                        event={event}
                                        userInfo={userInfo}
                                        organizerInfo={organizerInfo}
                                        onLikeChange={handleLikeChange}
                                    />
                                );
                            })
                        )}
                        {isFetchingNextPage && Array.from({ length: 3 }).map((_, i) => <EventCardSkeleton key={`more-${i}`} />)}
                    </div>

                    {hasNextPage && !isLoading && apiEvents.length > 0 && (
                        <div className="mt-8 flex justify-center">
                            <button
                                type="button"
                                onClick={() => fetchNextPage()}
                                disabled={isFetchingNextPage}
                                className="group inline-flex h-14 items-center gap-3 rounded-full bg-grey10 pl-7 pr-2 text-base font-semibold text-white transition-colors hover:bg-black disabled:opacity-60 dark:bg-white dark:text-nightBg dark:hover:bg-grey90"
                            >
                                {isFetchingNextPage ? 'Loading...' : 'Load more events'}
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 transition-transform group-hover:translate-y-0.5 dark:bg-black/10">
                                    <ArrowRight className="h-4 w-4 rotate-90" />
                                </span>
                            </button>
                        </div>
                    )}
                </section>

                {iframe ? null : (
                    <section className={`${wrap} mt-24 mb-20 sm:mt-32`}>
                        <div className="relative isolate overflow-hidden rounded-[36px] bg-[#0a0a0a] px-6 py-12 text-white dark:bg-[#101010] dark:ring-1 dark:ring-white/10 sm:px-12 sm:py-16 lg:px-16 lg:py-20">
                            <div className="pointer-events-none absolute -right-32 -top-32 -z-10 h-[420px] w-[420px] rounded-full bg-[radial-gradient(closest-side,rgba(34,201,205,0.3),transparent)]" />
                            <div className="pointer-events-none absolute -bottom-40 left-1/4 -z-10 h-[360px] w-[360px] rounded-full bg-[radial-gradient(closest-side,rgba(109,74,255,0.25),transparent)]" />
                            <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-center">
                                <div>
                                    <p className="text-sm font-bold uppercase tracking-[0.18em] text-nightAccent">For organizers</p>
                                    <h2 className="mt-4 text-[clamp(40px,5.5vw,76px)] font-bold leading-[0.95] tracking-[-0.05em]">
                                        Throw something <span className="text-nightAccent">unforgettable.</span>
                                    </h2>
                                    <p className="mt-6 max-w-md text-lg leading-relaxed text-white/60">
                                        Everything you need to sell out, from the first ticket to the last check-in.
                                    </p>
                                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                        <Link to="/create-event" className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-nightAccent px-7 text-base font-semibold text-nightBg transition-colors hover:bg-white">
                                            Create an event <ArrowRight className="h-4 w-4" />
                                        </Link>
                                        <Link to="/aboutus" className="inline-flex h-14 items-center justify-center gap-2 rounded-full px-7 text-base font-semibold text-white ring-1 ring-white/20 transition-colors hover:bg-white/10">
                                            About {getSiteName()}
                                        </Link>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {organizerFeatures.map(({ Icon, title, text }) => (
                                        <div key={title} className="rounded-3xl bg-white/[0.05] p-6 ring-1 ring-white/10 transition-colors hover:bg-white/[0.08]">
                                            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-nightAccent/15 text-nightAccent">
                                                <Icon className="h-5 w-5" />
                                            </span>
                                            <div className="mt-5 text-lg font-bold tracking-[-0.02em]">{title}</div>
                                            <p className="mt-1.5 text-sm leading-relaxed text-white/55">{text}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                )}
            </main>

            {iframe ? null : <Footer hasMobileNav />}
            {iframe ? null : <MobileBottomNav />}
        </div>
    );
}
